/**
 * Tracks which of several overlapping async operations is the most recently
 * started, AND owns the single "is a load outstanding" flag that drives the
 * certification detail pane's loading state.
 *
 * This exists because `ExamSettingsTab` fetches a certification's full
 * detail (`getTrack`) every time the admin selects a different row in
 * `CertificationList`. Fetches are not guaranteed to resolve in the order
 * they were started: click cert A, then click cert B before A's request
 * returns, and A's response can land AFTER B's. Without a guard, whichever
 * response arrives last silently wins and repopulates the form with the
 * wrong certification's data — while the sidebar still highlights the one
 * the admin actually selected. If Publish is then pressed, the save still
 * targets the right `trackId` in the URL (the backend keys off the path,
 * not the body) but silently carries the WRONG certification's field
 * values, overwriting its real configuration.
 *
 * Round 1 fixed that with a bare counter + `isCurrent(token)`, gating each
 * write site's own `setTrack`/`setBankSize`/`setBankSizeFor` calls. But the
 * loading FLAG itself (`loadingTrack`) was still hand-set independently at
 * each call site, and two of the three sites (`handleCreate`,
 * `handleArchive`'s clear branch) never reset it: they supersede an
 * in-flight `loadTrack` by bumping the token, but `loadTrack`'s own
 * `finally` then correctly refuses to touch a flag that no longer belongs to
 * it — and NOTHING else was left to turn it off. Loading stays stuck `true`
 * forever, hiding a form that is actually correctly populated behind
 * "Loading exam settings…", with no escape but selecting yet another
 * certification (impossible if none exists).
 *
 * The fix: the loading flag is no longer set by the caller at all. Starting
 * an operation via `startLoad()` or `beginQuiet()` is ALWAYS what decides
 * the flag's new value — true for a genuine async wait, false for a write
 * that already has its result in hand (or that deliberately never shows a
 * loading state, e.g. Publish, which keeps the form visible and disables it
 * via its own `saving` flag instead of swapping it out). Because every
 * operation that can supersede another is required to declare which kind it
 * is up front, there is no code path left where starting something new
 * fails to leave the flag in the correct state — an interrupted load's own
 * `settle()` arriving late is a no-op precisely because the operation that
 * superseded it already set the flag correctly itself.
 *
 * Pure logic aside from the one `setLoading` callback (supplied by the
 * component, typically a `useState` setter) — no DOM, no fetch, no timers —
 * so the state machine is directly unit-testable without a React testing
 * library, which this repo does not have and is not allowed to add.
 * `ExamSettingsTab` holds one guard for its whole lifetime
 * (`useRef(createLoadGuard(setLoadingTrack)).current`) and threads its
 * token through every place that writes `track`, `bankSize` or
 * `bankSizeFor`.
 *
 * Why a monotonic counter rather than comparing against `selectedId`: a
 * `selectedId` comparison alone cannot tell two operations for the SAME
 * certification apart, so re-selecting an id (or a later `getTrack`
 * re-fetch for that same id, e.g. after Publish) would look identical to
 * the operation it is meant to supersede. The counter always changes across
 * two `startLoad()`/`beginQuiet()` calls, even for the same id.
 */
export function createLoadGuard(setLoading: (loading: boolean) => void) {
  let current = 0;

  return {
    /**
     * Begin an ASYNC operation whose result isn't known yet (a `getTrack`
     * fetch about to be sent). Supersedes whatever came before and
     * immediately reports loading=true — there is now something to wait on.
     * The caller MUST call `settle(token)` once that fetch resolves or
     * rejects, whichever happens.
     */
    startLoad(): number {
      current += 1;
      setLoading(true);
      return current;
    },

    /**
     * Begin an operation that must NOT show a loading state — either
     * because its result is already in hand (the response of a
     * just-awaited create/archive call, about to be committed directly to
     * state with nothing left to wait on) or because it deliberately never
     * owns the loading pane (Publish, which keeps the form visible the
     * whole time). Supersedes whatever came before and immediately reports
     * loading=false, which is what guarantees an interrupted `startLoad()`
     * can never leave the flag stuck true: superseding it here retires it
     * completely, so its later `settle()` call becomes a genuine no-op
     * instead of the one place responsible for resetting a flag that
     * nobody remembered to reset.
     */
    beginQuiet(): number {
      current += 1;
      setLoading(false);
      return current;
    },

    /**
     * Call once `token`'s async work (started via `startLoad`) has a
     * result, success or failure. If a newer operation has since started —
     * of either kind — this is a no-op: that operation already owns the
     * loading flag's current value.
     */
    settle(token: number): void {
      if (token === current) setLoading(false);
    },

    /** True only while `token` is still the operation most recently started. */
    isCurrent(token: number): boolean {
      return token === current;
    },
  };
}

export type LoadGuard = ReturnType<typeof createLoadGuard>;
